import {
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useState } from "react";

interface UseSortableFieldsProps {
  localSteps: any[];
  currentStep: number;
  currentSubStep: number;
  setLocalSteps: (steps: any[]) => void;
}

export const useSortableFields = ({
  localSteps,
  currentStep,
  currentSubStep,
  setLocalSteps
}: UseSortableFieldsProps) => {
  const [activeField, setActiveField] = useState<any>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const fieldId = active.id;

    const stepData = localSteps[currentStep];
    const stepField = stepData?.StepFields?.find(
      (f: any) => `step-field-${f.Id}` === fieldId,
    );
    if (stepField) return setActiveField(stepField);

    const subStepData = stepData?.InverseParentSteps?.[currentSubStep];
    const subField = subStepData?.StepFields?.find(
      (f: any) => `substep-field-${f.Id}` === fieldId,
    );
    if (subField) setActiveField(subField);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return setActiveField(null);

    const activeId = String(active.id);
    const overId = String(over.id);

    if (
      activeId.startsWith("step-field-") &&
      overId.startsWith("step-field-")
    ) {
      reorderStepFields(activeId, overId);
    } else if (
      activeId.startsWith("substep-field-") &&
      overId.startsWith("substep-field-")
    ) {
      reorderSubStepFields(activeId, overId);
    }

    setActiveField(null);
  };

  const reorderStepFields = (activeId: string, overId: string) => {
    const updatedSteps = JSON.parse(JSON.stringify(localSteps));
    const stepData = updatedSteps[currentStep];

    if (!stepData?.StepFields) return;

    const oldIndex = stepData.StepFields.findIndex(
      (f: any) => String(f.Id) === activeId.replace("step-field-", ""),
    );
    const newIndex = stepData.StepFields.findIndex(
      (f: any) => String(f.Id) === overId.replace("step-field-", ""),
    );

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const reordered = arrayMove(stepData.StepFields, oldIndex, newIndex).map(
      (f: any, i: number) => ({ ...f, Order: i + 1 }),
    );
    updatedSteps[currentStep].StepFields = reordered;

    setLocalSteps(updatedSteps);
  };

  const reorderSubStepFields = (activeId: string, overId: string) => {
    const updatedSteps = JSON.parse(JSON.stringify(localSteps));
    const stepData = updatedSteps[currentStep];
    const subStepData = stepData?.InverseParentSteps?.[currentSubStep];
    if (!subStepData?.StepFields) return;

    const oldIndex = subStepData.StepFields.findIndex(
      (f: any) => String(f.Id) === activeId.replace("substep-field-", ""),
    );
    const newIndex = subStepData.StepFields.findIndex(
      (f: any) => String(f.Id) === overId.replace("substep-field-", ""),
    );

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const reordered = arrayMove(subStepData.StepFields, oldIndex, newIndex).map(
      (f: any, i: number) => ({ ...f, Order: i + 1 }),
    );

    updatedSteps[currentStep].InverseParentSteps[currentSubStep].StepFields =
      reordered;

    setLocalSteps(updatedSteps);
  };

  return { sensors, handleDragStart, handleDragEnd, activeField };
};
